import type { CreateBookingOption, CreateBookingStorageType } from "design-components";

export type PickupStaticWheeledVariant = "chassis" | "trailer" | "chassisAndContainer";

/** Static pickup equipment catalog until operator equipment API exists. */
export interface PickupStaticEquipment {
  /** Exact `pickup.equipmentDetails` sent to create-booking API. */
  equipmentDetails: string;
  equipmentName: string;
  equipmentSize: string;
  equipmentOwner: string;
  equipmentType: string;
  /** Display label in catalog (`Stack` | `Wheeled`). */
  storageType: string;
  wheeledVariant?: PickupStaticWheeledVariant;
  isContainerReefer?: boolean;
  containerNumber?: string;
  containerSize?: string;
  chassisId?: string;
  licensePlate?: string;
  isTrailerReefer?: boolean;
  trailerNumber?: string;
  trailerSize?: string;
}

export const PICKUP_STATIC_EQUIPMENT: readonly PickupStaticEquipment[] = [
  {
    equipmentDetails: "Container -CONT123456— 53' · ABC Logistics · Container · Stack",
    equipmentName: "Container - CONT123456",
    equipmentSize: "53'",
    equipmentOwner: "Owner A",
    equipmentType: "Container",
    storageType: "Stack",
    isContainerReefer: false,
    containerNumber: "CONT123456",
    containerSize: "40'",
  },
  {
    equipmentDetails: "Container -CONT789012— 40' · Maersk · Container · Stack",
    equipmentName: "Container - CONT789012",
    equipmentSize: "40'",
    equipmentOwner: "Maersk",
    equipmentType: "Container",
    storageType: "Stack",
    isContainerReefer: true,
    containerNumber: "CONT789012",
    containerSize: "40'",
  },
  {
    equipmentDetails: "Container -CONT445566— 20' · MSC · Container · Stack",
    equipmentName: "Container - CONT445566",
    equipmentSize: "20'",
    equipmentOwner: "MSC",
    equipmentType: "Container",
    storageType: "Stack",
    isContainerReefer: false,
    containerNumber: "CONT445566",
    containerSize: "20'",
  },
  {
    equipmentDetails: "Container -CONT998877— 45' · Hapag-Lloyd · Container · Stack",
    equipmentName: "Container - CONT998877",
    equipmentSize: "45'",
    equipmentOwner: "Hapag-Lloyd",
    equipmentType: "Container",
    storageType: "Stack",
    isContainerReefer: false,
    containerNumber: "CONT998877",
    containerSize: "45'",
  },
  {
    equipmentDetails: "Container -CONT112233— 53' · CMA CGM · Container · Stack",
    equipmentName: "Container - CONT112233",
    equipmentSize: "53'",
    equipmentOwner: "CMA CGM",
    equipmentType: "Container",
    storageType: "Stack",
    isContainerReefer: true,
    containerNumber: "CONT112233",
    containerSize: "53'",
  },
  {
    equipmentDetails: "Container -CONT556677— 40' · Evergreen · Container · Stack",
    equipmentName: "Container - CONT556677",
    equipmentSize: "40'",
    equipmentOwner: "Evergreen",
    equipmentType: "Container",
    storageType: "Stack",
    isContainerReefer: false,
    containerNumber: "CONT556677",
    containerSize: "40'",
  },
  {
    equipmentDetails: "Chasis-CH12345 — 53' · ABC Logistics · Container · Stack",
    equipmentName: "Chasis - CH12345",
    equipmentSize: "53'",
    equipmentOwner: "Optional Owner",
    equipmentType: "Chassis and Container",
    storageType: "Wheeled",
    wheeledVariant: "chassisAndContainer",
    chassisId: "CH123456",
    licensePlate: "ABC1234",
  },
  {
    equipmentDetails: "Vector - Chassis",
    equipmentName: "Vector - Chassis",
    equipmentSize: "28'",
    equipmentOwner: "RapidMove Inc",
    equipmentType: "Chassis",
    storageType: "Wheeled",
    wheeledVariant: "chassis",
    chassisId: "VEC-28",
    licensePlate: "VEC28",
  },
  {
    equipmentDetails: "Trailer-5643356 — 53' · ABC Logistics · Container · Stack",
    equipmentName: "Trailer - 5643356",
    equipmentSize: "53'",
    equipmentOwner: "Jack Quinns",
    equipmentType: "Trailer",
    storageType: "Wheeled",
    wheeledVariant: "trailer",
    isTrailerReefer: true,
    trailerNumber: "5643356",
    trailerSize: "50'",
    licensePlate: "EREE2",
  },
] as const;

const PICKUP_EQUIPMENT_ID_PREFIX = "pickup-eq-";
export const PICKUP_DETAIL_EQUIPMENT_ID_PREFIX = "pickup-detail-";

export function buildPickupEquipmentOptionId(index: number): string {
  return `${PICKUP_EQUIPMENT_ID_PREFIX}${index}`;
}

export function buildPickupDetailEquipmentOptionId(bookingId: number): string {
  return `${PICKUP_DETAIL_EQUIPMENT_ID_PREFIX}${bookingId}`;
}

export function isPickupDetailEquipmentOptionId(id: string | null | undefined): boolean {
  return Boolean(id?.startsWith(PICKUP_DETAIL_EQUIPMENT_ID_PREFIX));
}

/** Normalize equipmentDetails for fuzzy match (dash variants, spacing). */
export function normalizePickupEquipmentDetailsKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\u2010-\u2015\u2212–—]/g, "-")
    .replace(/\s+/g, " ")
    .replace(/\s*-\s*/g, "-")
    .replace(/\s*·\s*/g, " · ");
}

export interface PickupStaticEquipmentMatch {
  equipment: PickupStaticEquipment;
  optionId: string;
}

/**
 * Resolve static catalog row from GET pickup `equipmentDetails` and optional stack branch.
 * Falls back: exact → normalized → containerNumber (stack bookings).
 */
export function findPickupStaticEquipmentByDetails(
  equipmentDetails: string | null | undefined,
  stack?: { containerNumber?: string } | null,
): PickupStaticEquipmentMatch | null {
  const trimmed = equipmentDetails?.trim();
  if (trimmed) {
    const exactIndex = PICKUP_STATIC_EQUIPMENT.findIndex((row) => row.equipmentDetails.trim() === trimmed);
    if (exactIndex >= 0) {
      return toPickupStaticEquipmentMatch(exactIndex);
    }
    const normalizedNeedle = normalizePickupEquipmentDetailsKey(trimmed);
    const normalizedIndex = PICKUP_STATIC_EQUIPMENT.findIndex(
      (row) => normalizePickupEquipmentDetailsKey(row.equipmentDetails) === normalizedNeedle,
    );
    if (normalizedIndex >= 0) {
      return toPickupStaticEquipmentMatch(normalizedIndex);
    }
  }

  const containerNumber = stack?.containerNumber?.trim();
  if (containerNumber) {
    const containerIndex = PICKUP_STATIC_EQUIPMENT.findIndex(
      (row) => (row.containerNumber?.trim() ?? "") === containerNumber,
    );
    if (containerIndex >= 0) {
      return toPickupStaticEquipmentMatch(containerIndex);
    }
  }

  return null;
}

/** Resolve static catalog row from full GET pickup branch (AV-44 edit). */
export function findPickupStaticEquipmentFromPickupBranch(
  pickup: { equipmentDetails?: string; stack?: { containerNumber?: string } } | null | undefined,
): PickupStaticEquipmentMatch | null {
  if (!pickup) return null;
  return findPickupStaticEquipmentByDetails(pickup.equipmentDetails, pickup.stack);
}

function toPickupStaticEquipmentMatch(index: number): PickupStaticEquipmentMatch {
  return {
    equipment: PICKUP_STATIC_EQUIPMENT[index],
    optionId: buildPickupEquipmentOptionId(index),
  };
}

export function findPickupStaticEquipmentById(id: string | null | undefined): PickupStaticEquipment | null {
  if (!id?.startsWith(PICKUP_EQUIPMENT_ID_PREFIX)) return null;
  const index = Number(id.slice(PICKUP_EQUIPMENT_ID_PREFIX.length));
  if (!Number.isInteger(index) || index < 0) return null;
  return PICKUP_STATIC_EQUIPMENT[index] ?? null;
}

export interface PickupMultiPieceEquipmentWireRow {
  isReefer: boolean;
  containerNumber: string;
  containerSize?: string;
  equipmentOwner?: string;
}

/** Map static catalog row to create-booking multipiece wire shape (FR-AV132-API-003). */
export function toPickupMultiPieceWireRow(equipment: PickupStaticEquipment): PickupMultiPieceEquipmentWireRow {
  return {
    isReefer: equipment.isContainerReefer ?? equipment.isTrailerReefer ?? false,
    containerNumber:
      equipment.containerNumber?.trim() ||
      equipment.chassisId?.trim() ||
      equipment.trailerNumber?.trim() ||
      "",
    containerSize:
      equipment.containerSize?.trim() ||
      equipment.equipmentSize?.trim() ||
      equipment.trailerSize?.trim() ||
      undefined,
    equipmentOwner: equipment.equipmentOwner?.trim() || undefined,
  };
}

function matchesPickupMultiPieceWireRow(
  equipment: PickupStaticEquipment,
  row: PickupMultiPieceEquipmentWireRow,
): boolean {
  const wireRow = toPickupMultiPieceWireRow(equipment);
  const containerNumber = row.containerNumber.trim();
  if (wireRow.containerNumber !== containerNumber) return false;
  const containerSize = row.containerSize?.trim() ?? "";
  if (containerSize && (wireRow.containerSize?.trim() ?? "") !== containerSize) return false;
  const equipmentOwner = row.equipmentOwner?.trim() ?? "";
  if (equipmentOwner && (wireRow.equipmentOwner?.trim() ?? "") !== equipmentOwner) return false;
  return wireRow.isReefer === row.isReefer;
}

/** Match GET `multiPieceEquipmentDetails` rows to static catalog option ids (AV-132 edit). */
export function findPickupEquipmentIdsFromMultiPieceDetails(
  details: readonly PickupMultiPieceEquipmentWireRow[],
): string[] {
  return details
    .map((row) => findPickupEquipmentOptionIdFromMultiPieceRow(row))
    .filter((id): id is string => Boolean(id));
}

export function findPickupEquipmentOptionIdFromMultiPieceRow(
  row: PickupMultiPieceEquipmentWireRow,
): string | null {
  if (!row.containerNumber.trim()) return null;
  const index = PICKUP_STATIC_EQUIPMENT.findIndex((equipment) =>
    matchesPickupMultiPieceWireRow(equipment, row),
  );
  if (index < 0) return null;
  return buildPickupEquipmentOptionId(index);
}

/** Build display label for unmatched multipiece wire rows on edit pre-fill. */
export function formatPickupMultiPieceWireRowLabel(row: PickupMultiPieceEquipmentWireRow): string {
  const parts = [row.containerNumber.trim()];
  if (row.containerSize?.trim()) parts.push(row.containerSize.trim());
  if (row.equipmentOwner?.trim()) parts.push(row.equipmentOwner.trim());
  return parts.filter(Boolean).join(" · ");
}

export const PICKUP_MULTIPIECE_WIRE_ROW_ID_PREFIX = "pickup-multipiece-wire-";

export function buildPickupMultiPieceWireRowOptionId(index: number): string {
  return `${PICKUP_MULTIPIECE_WIRE_ROW_ID_PREFIX}${index}`;
}

export function isPickupMultiPieceWireRowOptionId(id: string | null | undefined): boolean {
  return Boolean(id?.startsWith(PICKUP_MULTIPIECE_WIRE_ROW_ID_PREFIX));
}

export function formatPickupEquipmentOptionLabel(equipment: PickupStaticEquipment): string {
  return `${equipment.equipmentName} — ${equipment.equipmentSize} · ${equipment.equipmentOwner} · ${equipment.equipmentType}`;
}

/** Keep selected equipment visible when location storage filter hides catalog rows (edit pre-fill). */
export function ensurePickupEquipmentOptionInList(
  options: CreateBookingOption[],
  equipmentId: string | null | undefined,
  fallbackLabel?: string | null,
): CreateBookingOption[] {
  if (!equipmentId || options.some((option) => option.value === equipmentId)) {
    return options;
  }
  const staticRow = findPickupStaticEquipmentById(equipmentId);
  const label = staticRow?.equipmentDetails.trim() || fallbackLabel?.trim();
  if (!label) return options;
  return [{ value: equipmentId, label }, ...options];
}

function normalizePickupEquipmentStorageType(raw: string): CreateBookingStorageType | null {
  const normalized = raw.trim().toLowerCase();
  if (normalized === "stack") return "stack";
  if (normalized === "wheeled") return "wheeled";
  return null;
}

export function resolvePickupStaticEquipmentStorageType(
  equipment: PickupStaticEquipment | null | undefined,
): CreateBookingStorageType | null {
  if (!equipment) return null;
  return normalizePickupEquipmentStorageType(equipment.storageType);
}

/** Scope equipment to the selected location storage type (same branch as dropoff capacity). */
export function filterPickupStaticEquipmentByStorageType(
  storageType: CreateBookingStorageType | null,
): PickupStaticEquipment[] {
  if (!storageType) return [...PICKUP_STATIC_EQUIPMENT];
  return PICKUP_STATIC_EQUIPMENT.filter(
    (row) => normalizePickupEquipmentStorageType(row.storageType) === storageType,
  );
}

export type PickupEquipmentOptionsScope = "all" | "containers_only";

function isPickupContainerListEquipment(equipment: PickupStaticEquipment): boolean {
  return equipment.equipmentType.trim().toLowerCase() === "container";
}

export function buildPickupEquipmentOptions(
  storageType: CreateBookingStorageType | null,
  scope: PickupEquipmentOptionsScope = "all",
): CreateBookingOption[] {
  return PICKUP_STATIC_EQUIPMENT.flatMap((equipment, index) => {
    if (storageType && normalizePickupEquipmentStorageType(equipment.storageType) !== storageType) {
      return [];
    }
    if (scope === "containers_only" && !isPickupContainerListEquipment(equipment)) {
      return [];
    }
    return [
      {
        value: buildPickupEquipmentOptionId(index),
        label: formatPickupEquipmentOptionLabel(equipment),
      },
    ];
  });
}

/** Map wizard multi-select ids to create-booking `multiPieceEquipmentDetails` (FR-AV132-API-003). */
export function buildPickupMultiPieceEquipmentDetails(
  equipmentIds: string[],
): PickupMultiPieceEquipmentWireRow[] {
  return equipmentIds
    .map((id) => findPickupStaticEquipmentById(id))
    .filter((equipment): equipment is PickupStaticEquipment => equipment != null)
    .map(toPickupMultiPieceWireRow);
}

/** Join selected catalog `equipmentDetails` for pickup multipiece POST (FR-AV132-API-002). */
export function composePickupMultipieceEquipmentDetailsLabel(equipmentIds: string[]): string {
  return equipmentIds
    .map((id) => findPickupStaticEquipmentById(id))
    .filter((equipment): equipment is PickupStaticEquipment => equipment != null)
    .map((equipment) => equipment.equipmentDetails.trim())
    .filter(Boolean)
    .join(", ");
}
